//@flow
import React, { PureComponent } from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';

import {
    RiderPersonaResourceLimit,
    UnitEnum
} from 'common/models/internal/RiderPersonaResourceLimit';
import { ResourceLimit } from 'common/models/internal/ResourceLimit';
import {
    VolumeLimit,
    VolumeLimitUnitEnum
} from 'common/models/internal/VolumeLimit';

import defaultTheme from './RiderPersonaLimits.scss';

import ResourceLimitInput from 'common/entity/components/resourceLimit/ResourceLimitInput';
import { Select, SelectTypes } from 'common/components/core/select/Select';
import TransactionTimeInput from 'common/entity/generic/TransactionTimeInput';

import DataUtils from 'common/utils/DataUtils';
import testIds from 'common/tests/entity/riderPersonaMasterIds.json';
import { CustomField } from 'common/models/internal/EntitySettings';

const defaultOptionalResourceList = [
    'volume',
    'distance',
    'weight',
    'travelTime',
    'totalTime',
    'location'
];

const RESOURCES = Object.freeze({
    totalTime: 'totalTime',
    travelTime: 'travelTime',
    weight: 'weight',
    volume: 'volume',
    location: 'location'
});

class RiderPersonaLimits extends PureComponent {
    state = {
        optionalResourceList: new Set(),
        orderedSelectedOptionalResourceList: new Set(),
        selectedCurrResource: null
    };

    componentDidMount() {
        const { limits, customFields } = this.props;
        const { resourceLimits } = limits;
        const orderedSelectedOptionalResourceList = new Set();
        const optionalResourceList = new Set();
        defaultOptionalResourceList.forEach(name => {
            const currentLimit = limits.get(name);
            if (
                DataUtils.isEmpty(currentLimit?.lowerThresh) &&
                DataUtils.isEmpty(currentLimit?.higherThresh)
            ) {
                optionalResourceList.add(name);
            } else {
                orderedSelectedOptionalResourceList.add(name);
            }
        });

        //Adding custom resources defined in client setting
        customFields
            .filter(field => field.metadata.isResource)
            .forEach(customResource => {
                const currentLimit = limits.resourceLimits.find(
                    resource => resource.name === customResource.key
                );
                if (
                    DataUtils.isEmpty(currentLimit?.lowerThresh) &&
                    DataUtils.isEmpty(currentLimit?.higherThresh)
                ) {
                    optionalResourceList.add(customResource.key);
                } else {
                    orderedSelectedOptionalResourceList.add(customResource.key);
                }
            });

        //Adding resources added through API or the ones that were removed from settings
        resourceLimits.forEach(resource => {
            orderedSelectedOptionalResourceList.add(resource.name);
        });
        this.setState({
            optionalResourceList,
            orderedSelectedOptionalResourceList
        });
    }

    getDummyResourceObject = unit => {
        const dummyResource = RiderPersonaResourceLimit.defaultInit({
            lowerThresh: 0
        });

        return unit ? dummyResource.setUnit(unit) : dummyResource;
    };

    getDummyResource = name => {
        switch (name) {
            case RESOURCES.volume: {
                return VolumeLimit.defaultInit({
                    lowerThresh: 0
                }).setUnit(VolumeLimitUnitEnum.METERS_CUBIC);
            }
            case RESOURCES.weight: {
                return this.getDummyResourceObject(UnitEnum.KG);
            }
            case RESOURCES.travelTime:
            case RESOURCES.totalTime: {
                return this.getDummyResourceObject(UnitEnum.SECONDS);
            }
            default:
                return this.getDummyResourceObject();
        }
    };

    getDummyCustomResource = (): ResourceLimit => {
        return ResourceLimit.defaultInit();
    };

    handleResourceLimitChange = ({ name, higherThresh, lowerThresh }) => {
        const { limits, unitConverter, onChange, selectedUnit } = this.props;
        const isCustomResource = this.isCustomResource(name);
        let resource = this.getResourceObject(name);
        let updatedLimits = limits;
        resource = resource
            .setHigherThresh(higherThresh)
            .setLowerThresh(lowerThresh)
            .setAllValidationErrors(); // Hack to set validation errors after setting higher and lower limits
        if (isCustomResource) {
            const { resourceLimits } = limits;
            const updatedResourceLimits = resourceLimits.map(resourceLimit => {
                if (resourceLimit.name === name) {
                    return resource;
                }
                return resourceLimit;
            });
            updatedLimits = limits
                .setIn(['resourceLimits'], updatedResourceLimits)
                .convertToSourceUnit(unitConverter, selectedUnit);
        } else {
            updatedLimits = limits
                .setIn([name], resource)
                .convertToSourceUnit(unitConverter, selectedUnit);
        }
        onChange && onChange(updatedLimits);
    };

    removeResource = resourceName => {
        const {
            optionalResourceList,
            orderedSelectedOptionalResourceList
        } = this.state;

        const { limits, onChange, unitConverter } = this.props;

        const updatedOrderedSelectedOptionalResourceList = new Set(
            orderedSelectedOptionalResourceList.values()
        );
        updatedOrderedSelectedOptionalResourceList.delete(resourceName);
        const updatedOptionalResourceList = new Set(
            optionalResourceList.values()
        );
        updatedOptionalResourceList.add(resourceName);
        this.setState({
            optionalResourceList: updatedOptionalResourceList,
            orderedSelectedOptionalResourceList: updatedOrderedSelectedOptionalResourceList
        });

        const updatedLimits = limits.convertToSourceUnit(unitConverter, {});
        const isCustomResource = this.isCustomResource(resourceName);
        if (isCustomResource) {
            const updatedCustomResources = updatedLimits.resourceLimits.filter(
                limit => limit.name !== resourceName
            );
            return onChange?.(
                updatedLimits.setIn(['resourceLimits'], updatedCustomResources)
            );
        }

        onChange?.(updatedLimits.setIn([resourceName], null));
    };

    addResource = () => {
        const {
            selectedCurrResource,
            optionalResourceList,
            orderedSelectedOptionalResourceList
        } = this.state;

        if (!selectedCurrResource) {
            return;
        }
        const { limits, onChange, unitConverter } = this.props;
        const updatedOptionalResourceList = new Set(
            optionalResourceList.values()
        );
        updatedOptionalResourceList.delete(selectedCurrResource);
        const updatedOrderedSelectedOptionalResourceList = new Set(
            orderedSelectedOptionalResourceList.values()
        );
        updatedOrderedSelectedOptionalResourceList.add(selectedCurrResource);
        this.setState({
            optionalResourceList: updatedOptionalResourceList,
            orderedSelectedOptionalResourceList: updatedOrderedSelectedOptionalResourceList,
            selectedCurrResource: null
        });
        const updatedLimits = limits.convertToSourceUnit(unitConverter, {});
        const isCustomResource = this.isCustomResource(selectedCurrResource);
        if (isCustomResource) {
            const updatedCustomResources = updatedLimits.resourceLimits.push(
                this.getDummyCustomResource()
                    .setName(selectedCurrResource)
                    .setLowerThresh(0)
                    .setAllValidationErrors()
            );
            return onChange?.(
                updatedLimits.setIn(['resourceLimits'], updatedCustomResources)
            );
        }

        onChange?.(
            updatedLimits.setIn(
                [selectedCurrResource],
                this.getDummyResource(selectedCurrResource)
            )
        );
    };

    onSelectResource = value => {
        this.setState({
            selectedCurrResource: value?.value
        });
    };

    getOptions = () => {
        const { optionalResourceList } = this.state;
        const { t } = this.props;
        return [...optionalResourceList.keys()].sort().map(option => {
            const isCustomResource = this.isCustomResource(option);
            const customResourceField = this.getCustomResourceField(option);
            const label = isCustomResource
                ? customResourceField.name
                : t(`masterRiderPersonaProperties.resourceDropdown.${option}`);
            return {
                label,
                value: option
            };
        });
    };

    isCustomResource = resourceId => {
        const allKnownResourceLimits = ['task', ...defaultOptionalResourceList];
        return !allKnownResourceLimits.includes(resourceId);
    };

    getCustomResourceField = fieldId => {
        const { customFields } = this.props;
        const customResourceField = customFields.find(
            resource => resource.key === fieldId
        );
        if (customResourceField) {
            return customResourceField;
        }
        //Handling the case of resource not defined in client settings
        return CustomField.fromJS({ name: fieldId, key: fieldId });
    };

    getResourceObject = resourceId => {
        const isCustomResource = this.isCustomResource(resourceId);
        const { limits } = this.props;
        if (isCustomResource) {
            return (
                limits.resourceLimits.find(
                    resource => resource.name === resourceId
                ) || this.getDummyResource(resourceId)
            );
        }
        return limits.getIn([resourceId]) || this.getDummyResource(resourceId);
    };

    renderResource = options => {
        const { limits, unitConverter, t, theme } = this.props;
        const { name, hideRightIcon } = options;
        const isCustomResource = this.isCustomResource(name);
        const resource = this.getResourceObject(name);
        const customResourceField = this.getCustomResourceField(name);
        const label = isCustomResource
            ? customResourceField.name
            : t(`masterRiderPersonaProperties.${name}`);
        const lowerThreshPlaceHolder = isCustomResource
            ? `Min ${customResourceField.name}`
            : t('masterRiderPersonaProperties.lowerThreshPlaceHolder', {
                  name: t(`masterRiderPersonaProperties.${name}`)
              });
        const higherThreshPlaceHolder = isCustomResource
            ? `Max ${customResourceField.name}`
            : t('masterRiderPersonaProperties.higherThreshPlaceHolder', {
                  name: t(`masterRiderPersonaProperties.${name}`)
              });
        const unit =
            name === 'volume' || isCustomResource
                ? ' '
                : limits.getPreferredUnitText(unitConverter, name);
        return (
            <ResourceLimitInput
                key={name}
                theme={theme}
                className={theme.fieldGroup}
                resourceLimit={resource}
                resourceName={name}
                testId={testIds.inputs.limits[name]}
                label={label}
                unit={unit}
                lowerThreshPlaceHolder={lowerThreshPlaceHolder}
                higherThreshPlaceHolder={higherThreshPlaceHolder}
                rightIcon={
                    !hideRightIcon ? classnames('delete-bin', theme.delete) : ''
                }
                handleRightIconClick={this.removeResource}
                onChange={this.handleResourceLimitChange}
            />
        );
    };

    renderOptionalResources = () => {
        const {
            selectedCurrResource,
            orderedSelectedOptionalResourceList
        } = this.state;
        const { t, theme } = this.props;
        const options = this.getOptions();
        return (
            <div className={theme.optionalResourceLimitWrapper}>
                <div className={theme.optionalResources}>
                    {[...orderedSelectedOptionalResourceList.keys()].map(name =>
                        this.renderResource({ name })
                    )}
                </div>
                {options?.length ? (
                    <div className={theme.addResourceWrapper}>
                        <Select
                            placeholder={t(
                                'masterRiderPersonaProperties.selectResource'
                            )}
                            theme={theme}
                            className={theme.selectResource}
                            testId={
                                testIds.inputs.limits.optionalResourcesInput
                            }
                            isClearable
                            selectType={SelectTypes.Outline}
                            options={options}
                            value={selectedCurrResource}
                            onChange={this.onSelectResource}
                        />
                        <span
                            className={classnames(theme.addResource, {
                                [theme.disabled]: !selectedCurrResource
                            })}
                            onClick={this.addResource}
                        >
                            {`+ ${t(
                                'masterRiderPersonaProperties.addResource'
                            )}`}
                        </span>
                    </div>
                ) : null}
            </div>
        );
    };
    handleTransactionTimeChange = value => {
        const { limits, onChange, unitConverter, selectedUnit } = this.props;
        onChange?.(
            limits
                .setTransactionTimePerTask(value * 60)
                .convertToSourceUnit(unitConverter, selectedUnit)
        );
    };
    render() {
        const { theme, limits, t } = this.props;
        return (
            <div className={theme.limitWrapper}>
                {this.renderResource({ name: 'task', hideRightIcon: true })}
                {this.renderOptionalResources()}
                <TransactionTimeInput
                    t={t}
                    testId={testIds.inputs.limits.transactionTimeInput}
                    theme={theme}
                    handleRightIconClick={this.removeResource}
                    onChange={this.handleTransactionTimeChange}
                    data={limits?.getIn(['transactionTimePerTask'])}
                />
            </div>
        );
    }
}

export default themr('RiderPersonaLimits', defaultTheme)(RiderPersonaLimits);
